/*
 *  This file common-dropdown.service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2022
 *  (C) 2015-2022. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *
 */

import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import moment from 'moment';
import { forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';
import { TSubscribeModuleResponse } from 'src/app/modules/team/team-dashboards/team-dashboards.model';
import { HroneGlobalStateService } from 'src/app/store/global/global-state.service';
import { TMonth } from '../../components/month-selector/month-selector.component';
import { FinancialYearModel, TKeyValue } from '../../models/common.models';
import { HttpService } from '../http-service';

export const commonDdUrls = {
    currency: '/api/core/Currency',
    gender: '/api/core/Gender',
    skills: '/api/core/Skills',
    bloodGroups: '/api/core/BloodGroups',
    nationality: '/api/core/Nationality',
    employeeType: '/api/core/EmployeeType',
    employeeStatus: '/api/core/EmployeeStatus',
    specificEmpStatus: '/api/core/EmployeeStatus/Specific',
    salutation: '/api/core/Salutation',
    maritalStatus: '/api/core/MaritalStatus',
    parentsMediclaim: '/api/core/ParentsMediclaim',
    qualification: '/api/core/Qualification',
    qualificationType: '/api/core/QualificationType',
    specialization: '/api/core/Specialization',
    nominationType: '/api/core/NominationType',
    relationshipType: '/api/core/Relationship',
    documentType: '/api/workforce/Document/Type',
    industryType: '/api/core/IndustryType',
    months: '/api/payroll/MonthYear/',  // add parameter like -1, 0, etc
    monthList: '/api/core/global/Month',
    yearList: '/api/core/global/Year',
    payMode: '/api/core/PayMode',
    banks: '/api/core/Bank',
    branchesBankWise: '/api/core/BankBranch/BindDropDown',
    weekDays: '/api/core/global/Weekdays',
    financialYear: '/api/payroll/FinancialYear',
    financialYearList: '/api/payroll/FinancialYearList',
    otherStatus: `/api/core/EmployeeOtherStatus`,
    religion: `/api/core/Religion`,
    socialCategory: `/api/core/SocialCategory`,
    disabilityType: `/api/core/Disability`,
    language: `/api/core/Language`,
    languageProficiency: `/api/core/LanguageProficiency`
};

@Injectable({
    providedIn: 'root'
})
export class CommonDropdownService {

    moduleDd: TKeyValue[] = [];
    currencyDd: TKeyValue[] = [];
    genderDd: (TKeyValue & { isSelected?: boolean })[] = [];
    socialCategoryDd: (TKeyValue & { isSelected?: boolean })[] = [];
    skillsDd: TKeyValue[] = [];
    bloodGroupsDd: TKeyValue[] = [];
    nationalityDd: TKeyValue[] = [];
    employeeTypeDd: TKeyValue[] = [];
    employeeStatusDd: TKeyValue[] = [];
    specificEmpStatusDd: TKeyValue[] = [];
    specificEmpOtherStatusDd: TKeyValue[] = [];
    salutationDd: TKeyValue[] = [];
    maritalStatusDd: TKeyValue[] = [];
    parentsMediclaimDd: TKeyValue[] = [];
    qualificationDd: { key: string; value: string; isOther: boolean }[] = [];
    qualificationTypeDd: TKeyValue[] = [];
    specializationDd: TKeyValue[] = [];
    nominationTypeDd: TKeyValue[] = [];
    relationshipTypeDd: { key: number; value: string; isOnPersonal: boolean, isOnHealthInsurance: boolean, isOnFamily: boolean }[] = [];
    documentTypeDd: TKeyValue[] = [];
    industryTypeDd: TKeyValue[] = [];
    monthDd: TMonth[] = [];
    monthsDd: MonthsYearModel[] = [];  // genral
    monthOnlyDd: { key: number; value: string }[] = [];
    yearOnlyDd: { key: number; value: string }[] = [];
    payModeDd: TKeyValue<string, string>[] = [];
    bankDd: TKeyValue<string, string>[] = [];
    branchBankWiseDd: BankBranchDDModel[] = [];
    religionDd: TKeyValue<string, string>[] = [];
    weekDaysDd: TKeyValue[] = [];
    financialYearDd: FinancialYearDDModel[] = [];
    currentFinancialYear: FinancialYearDDModel | null = null;
    moduleLicenseStatus: { key: number, value: string, planId: number }[] = []
    financialYearListDd: FinancialYearModel[] = [];
    // Request deduplication for concurrent calls
    private financialYearListRequest: Promise<any> | null = null;
    private currencyDropDownRequest: Promise<TKeyValue[]> | null = null;
    private moduleLicenseStatusRequest: Promise<{ key: number, value: string, planId: number }[]> | null = null;
    religionDropdown: TKeyValue<number, string>[] = [];
    socialCategoryDropdown: TKeyValue<number, string>[] = [];
    disabilityDropdown: { key: number; value: string; description: string }[] = [];
    languageDropdown: TKeyValue<number, string>[] = [];
    languageProficiencyDropdown: TKeyValue<number, string>[] = [];
    constructor(
        private readonly _httpService: HttpService,
        public _globalStateService: HroneGlobalStateService,
    ) { }

    getModuleDropDown(): Promise<Array<TKeyValue>> {
        this.moduleDd = [];

        return new Promise((resolve, reject) => {
            this._globalStateService.subscribeModule().pipe(first()).
                subscribe(
                    (response: TSubscribeModuleResponse) => {
                        const list = response.subscribeModule ? response.subscribeModule : [];
                        this.moduleDd = _.sortBy(list.map(mapper => ({
                            value: mapper.moduleName, key: mapper.moduleId,
                        })), [e => e.value.toLowerCase()]) as TKeyValue[];

                        resolve(this.moduleDd);
                    }, err => {
                        reject(new Error("error!"));
                    });

        });
    }

    getCurrencyDropDown(): Promise<TKeyValue[]> {
        // If data is already available, return resolved promise with existing data
        if (this.currencyDd.length) {
            return Promise.resolve(this.currencyDd);
        }

        // If there's already a pending request, return the same promise
        if (this.currencyDropDownRequest) {
            return this.currencyDropDownRequest;
        }

        // Create new promise and cache it
        this.currencyDropDownRequest = new Promise((resolve, reject) => {
            this._httpService.Get(commonDdUrls.currency).subscribe({
                next: (response) => {
                    this.currencyDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                        value: mapper.text, key: mapper.id,
                    })), [e => e.value.toLowerCase()]);

                    // Clear the cached promise after resolving
                    this.currencyDropDownRequest = null;
                    resolve(this.currencyDd);
                },
                error: (err) => {
                    // Clear the cached promise on error
                    this.currencyDropDownRequest = null;
                    reject(err);
                }
            });
        });

        return this.currencyDropDownRequest;
    }

    getGenderDropDown() {

        if (this.genderDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.gender).subscribe((response) => {
            this.genderDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getSocialCategoryDropDown() {
        if (this.socialCategoryDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.socialCategory).subscribe((response) => {
            this.socialCategoryDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getSkillsDropDown() {

        if (this.skillsDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.skills).subscribe((response) => {
            this.skillsDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getBloodGroupDropDown() {

        if (this.bloodGroupsDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.bloodGroups).subscribe((response) => {
            this.bloodGroupsDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getNationalityDropDown() {
        if (this.nationalityDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.nationality).subscribe((response) => {
            this.nationalityDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getModuleLicenseStatus(): Promise<{ key: number, value: string, planId: number }[]> {

        if (this.moduleLicenseStatus?.length) {
            return Promise.resolve(this.moduleLicenseStatus);
        }

        if (this.moduleLicenseStatusRequest) {
            return this.moduleLicenseStatusRequest;
        }

        const url = '/api/SubscribeModule/License'
        this.moduleLicenseStatusRequest = new Promise((resolve, reject) => {
            this._httpService.Get(url).subscribe({
                next: (res: { moduleId: number, moduleName: string, planId: number }[]) => {
                    this.moduleLicenseStatus = res?.map(module => ({ key: module.moduleId, value: module.moduleName, planId: module.planId }))
                    this.moduleLicenseStatusRequest = null;
                    resolve(this.moduleLicenseStatus)
                },
                error: () => {
                    this.moduleLicenseStatusRequest = null;
                    reject(new Error('error!'));
                }
            })
        })

        return this.moduleLicenseStatusRequest;
    }

    getEmployeeTypeDropDown() {
        return new Promise((resolve, reject) => {

            if (this.employeeTypeDd.length) {
                resolve(true);
                return;
            }

            this._httpService.Get(commonDdUrls.employeeType).subscribe((response) => {
                this.employeeTypeDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                    value: mapper.text, key: mapper.id,
                })), [e => e.value.toLowerCase()]);

                resolve(true);
            });

        });
    }

    getEmployeeStatusDropDown(): Promise<TKeyValue[]> {
        return new Promise((resolve, reject) => {
            if (!this.employeeStatusDd.length) {
                this._httpService.Get(commonDdUrls.employeeStatus).toPromise().then((response) => {
                    this.employeeStatusDd = response.map((mapper: { id: number; text: string }) => ({
                        value: mapper.text, key: mapper.id,
                    }));

                    resolve(this.employeeStatusDd);
                });
            } else {
                resolve(this.employeeStatusDd);
            }
        });
    }

    getSpecificEmpStatusDropDown(): Promise<TKeyValue[]> {

        return new Promise((resolve, reject) => {

            if (!this.specificEmpStatusDd.length) {
                this._httpService.Get(commonDdUrls.specificEmpStatus).toPromise().then((response) => {
                    this.specificEmpStatusDd = response.map((mapper: { id: number; text: string }) => ({
                        value: mapper.text, key: mapper.id,
                    }));

                    resolve(this.specificEmpStatusDd);
                });
            } else {
                resolve(this.specificEmpStatusDd);
            }

        });
    }

    getSalutationDropDown() {

        if (this.salutationDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.salutation).subscribe((response) => {
            this.salutationDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getMaritalStatusDropDown() {

        if (this.maritalStatusDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.maritalStatus).subscribe((response) => {
            this.maritalStatusDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getParentsMediclaimDropDown() {

        if (this.parentsMediclaimDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.parentsMediclaim).subscribe((response) => {
            this.parentsMediclaimDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getQualificationDropDown() {

        if (this.qualificationDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.qualification).subscribe((response) => {
            this.qualificationDd = _.sortBy(response.map((mapper: { id: string; text: string; isOther: boolean }) => ({
                value: mapper.text, key: mapper.id, isOther: mapper.isOther
            })), [e => e.value.toLowerCase()]);
        });
    }

    getQualificationTypeDropDown() {

        if (this.qualificationTypeDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.qualificationType).subscribe((response) => {
            this.qualificationTypeDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getSpecializationDropDown(qualificationId: string) {

        if (this.specializationDd.length) {
            return;
        }

        const url = commonDdUrls.specialization + '/?qualificationCode=' + qualificationId;
        this._httpService.Get(url).subscribe((response) => {
            this.specializationDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getNominationTypeDropDown() {

        if (this.nominationTypeDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.nominationType).subscribe((response) => {
            this.nominationTypeDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getRelationshipTypeDropDown() {

        if (this.relationshipTypeDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.relationshipType).subscribe((response) => {
            this.relationshipTypeDd = _.sortBy(response.map((mapper: {
                id: number; text: string; isMannual: boolean; isOnPersonal: boolean,
                isOnHealthInsurance: boolean,
                isOnFamily: boolean
            }) => ({
                value: mapper.text, key: mapper.id, isOnPersonal: mapper.isOnPersonal, isOnHealthInsurance: mapper.isOnHealthInsurance, isOnFamily: mapper.isOnFamily
            })), [e => e.value?.toLowerCase()]);
        });
    }

    getDocumentTypeDropDown() {

        if (this.documentTypeDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.documentType).subscribe((response) => {
            this.documentTypeDd = _.sortBy(response.map((mapper: { id: number; text: string; isMannual: boolean }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }
    getIndustryTypeDropDown() {

        if (this.industryTypeDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.industryType).subscribe((response) => {
            this.industryTypeDd = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getPayModeDropDown() {

        if (this.payModeDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.payMode).subscribe((response) => {
            const list = response ? response : [];
            this.payModeDd = _.sortBy(list.map((mapper: { id: string; text: string }) => ({
                value: mapper.text, key: mapper.id,
            })), [e => e.value.toLowerCase()]);
        });
    }

    getBankDropDown() {

        if (this.bankDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.banks).subscribe((response) => {
            let list: { bankCode: string; bankName: string; bankAddress: string; isActive: boolean }[] = response ? response : [];
            list = _.sortBy(list, [e => e.bankName.toLowerCase()]);
            const filteredList = list.filter(data => data.isActive);
            this.bankDd = filteredList.map(mapper => ({ value: mapper.bankName, key: mapper.bankCode })) as TKeyValue<string, string>[];
        });
    }

    getBranchBankWiseDropDown(bankCode: string) {
        this.branchBankWiseDd = [];

        const reqBody = {
            "bankBranchCode": "",
            "bankCode": bankCode,
            "bankBranchStatus": "1",
            "pagination": {
                "pageNumber": 0,
                "pageSize": 0
            }
        };

        this._httpService.Post(commonDdUrls.branchesBankWise, reqBody).subscribe((response) => {
            const list: BankBranchDropdownModel[] = response ? response : [];
            this.branchBankWiseDd = _.sortBy(list.map(mapper => ({
                value: mapper.branchName, key: mapper.bankBranchCode, ifsc: mapper.indianFinancialSystemCode,
                bsrc: mapper.branchBasicStatisticalReturnsCode
            })), [e => e.value.toLowerCase()]);
        });
    }

    // get financial year month
    getMonthDropdown(key?: number): Promise<TMonth[]> {
        const p = key ? key : -1;
        return new Promise((resolve, reject) => {
            this.monthDd = [];
            this._httpService.Get(commonDdUrls.months + p).subscribe(response => {
                this.monthDd = response;

                resolve(this.monthDd);
            }, err => {
                reject(new Error());
            });
        });
    }

    // get normal month
    getMonthsDropdown(): Promise<any> {

        return new Promise((resolve, reject) => {

            if (this.monthsDd.length) {
                resolve(this.monthsDd);
            } else {
                const monthRequest = this._httpService.Get(commonDdUrls.monthList);
                const yearRequest = this._httpService.Get(commonDdUrls.yearList);

                forkJoin([monthRequest, yearRequest])
                    .toPromise().then(response => {
                        const months = response![0] as { monthId: number; monthName: string }[];
                        const years = response![1] as { year: number }[];

                        this.monthOnlyDd = months.map((mapper) => ({
                            value: mapper.monthName, key: Number(mapper.monthId)
                        }));
                        this.yearOnlyDd = years.map((mapper) => ({
                            value: String(mapper.year), key: Number(mapper.year)
                        }));

                        for (let ele1 of years) {
                            for (let ele2 of months) {
                                const monthNumber = ele2.monthId - 1;
                                let date1 = new Date(ele1.year, monthNumber, 1, 0, 0, 0, 0);
                                const mom = moment(date1).startOf("month");
                                let date = mom.format('YYYY-MM-DD') + 'T00:00:00';
                                this.monthsDd.push({
                                    year: ele1.year,
                                    monthId: ele2.monthId,
                                    monthName: ele2.monthName,
                                    date: date,
                                    moment: mom,
                                    label: ele2.monthName + ', ' + ele1.year
                                });
                            }
                        }

                        this.monthsDd.reverse();
                        resolve(this.monthsDd);
                    }).catch(err => {
                        resolve(this.monthsDd);
                    });
            }
        });
    }

    getReligionDropDown(): void {
        this.religionDd = [
            { key: 'hindu', value: 'Hindu' },
            { key: 'muslim', value: 'Muslim' }
        ] as TKeyValue<string, string>[];
    }

    getWeekDaysDropDown(): void {

        if (this.weekDaysDd.length) {
            return;
        }

        this._httpService.Get(commonDdUrls.weekDays).subscribe((response) => {
            this.weekDaysDd = _.sortBy(response.map((mapper: { weekId: number; weekName: string }) => ({
                value: mapper.weekName, key: mapper.weekId,
            })));
        });
    }

    getFinancialYearDd(): Promise<FinancialYearDDModel[]> {
        return new Promise((resolve, reject) => {
            if (this.financialYearDd.length) {
                resolve(this.financialYearDd);
            } else {
                this._httpService.Get(commonDdUrls.financialYear).subscribe(response => {
                    if (response?.length) {
                        this.financialYearDd = response.map((mapper: { id: number; name: string; currentFinancialYear: boolean; startDate: string; endDate: string; }) => ({
                            value: mapper.name, key: mapper.id, startDate: mapper.startDate, endDate: mapper.endDate,
                            currentFinancialYear: mapper.currentFinancialYear
                        }));
                        resolve(this.financialYearDd);
                    } else {
                        throw new Error('Error');
                    }
                }, err => {
                    reject(err);
                });
            }
        });
    }

    getFinancialYearList(): Promise<any> {
        // If we have cached data, return it 
        if (this.financialYearListDd.length) {
            const data = JSON.parse(JSON.stringify(this.financialYearListDd));
            return Promise.resolve(data);
        }

        // If there's already a request in progress, return that promise
        if (this.financialYearListRequest) {
            return this.financialYearListRequest;
        }

        // Create new request
        this.financialYearListRequest = new Promise((resolve, reject) => {
            this._httpService.Get(commonDdUrls.financialYearList).subscribe((response: FinancialYearModel[]) => {
                if (Array.isArray(response)) {
                    this.financialYearListDd = response;
                    this.financialYearListRequest = null; // Clear the request reference
                    const data = JSON.parse(JSON.stringify(this.financialYearListDd));
                    resolve(data);
                } else {
                    this.financialYearListRequest = null; // Clear the request reference
                    reject(new Error('Invalid response format'));
                }
            }, err => {
                this.financialYearListRequest = null; // Clear the request reference
                reject(err);
            });
        });

        return this.financialYearListRequest;
    }

    // Method to refresh financial year list (clears cache and makes new request)
    refreshFinancialYearList(): Promise<any> {
        this.financialYearListDd = [];
        this.financialYearListRequest = null;
        return this.getFinancialYearList();
    }

    getCurrentFinancialYear(): Promise<FinancialYearDDModel> {
        return new Promise((resolve, reject) => {
            this.getFinancialYearDd().then(res => {
                if (this.currentFinancialYear) {
                    resolve(this.currentFinancialYear);
                } else {
                    if (res && res.length) {
                        this.currentFinancialYear = res.find(ele => ele.currentFinancialYear) || null;
                    }

                    if (this.currentFinancialYear) {
                        resolve(this.currentFinancialYear);
                    } else {
                        throw new Error('Error');
                    }
                }
            }).catch(err => {
                reject(err);
            });
        });
    }

    isNewFormLabelFY(): Promise<boolean> {
        return this.getCurrentFinancialYear()
            .then(fy => new Date(fy.startDate).getFullYear() === 2026)
            .catch(() => false);
    }

    resetFinancialYearCache(): void {
        this.financialYearDd = [];
        this.currentFinancialYear = null;
        this.financialYearListDd = [];
        this.financialYearListRequest = null;
    }

    getSpecificEmpOtherStatusDropDown(): Promise<TKeyValue[]> {

        return new Promise((resolve, reject) => {

            if (!this.specificEmpOtherStatusDd.length) {
                this._httpService.Get(commonDdUrls.otherStatus).toPromise().then((response) => {
                    this.specificEmpOtherStatusDd = response.map((mapper: { id: number; text: string }) => ({
                        value: mapper.text, key: mapper.id,
                    })).sort((a: TKeyValue, b: TKeyValue) => a.value.localeCompare(b.value))

                    resolve(this.specificEmpOtherStatusDd);
                });
            } else {
                resolve(this.specificEmpOtherStatusDd);
            }

        });
    }
    fetchAndSetReligionDropDown(): Promise<TKeyValue<number, string>[]> {
        return new Promise((resolve, reject) => {
            if (this.religionDropdown.length) {
                resolve(this.religionDropdown);
            } else {
                this._httpService.Get(commonDdUrls.religion).subscribe((response) => {
                    this.religionDropdown = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                        value: mapper.text, key: mapper.id,
                    })), [e => e.value.toLowerCase()]);
                    resolve(this.religionDropdown);
                }, err => {
                    reject(err);
                });
            }
        });
    }
    fetchSocialCategoryDropDown(): Promise<TKeyValue<number, string>[]> {
        return new Promise((resolve, reject) => {
            if (this.socialCategoryDropdown.length) {
                resolve(this.socialCategoryDropdown);
            } else {
                this._httpService.Get(commonDdUrls.socialCategory).subscribe((response) => {
                    this.socialCategoryDropdown = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                        value: mapper.text, key: mapper.id,
                    })), [e => e.value.toLowerCase()]);
                    resolve(this.socialCategoryDropdown);
                }, err => {
                    reject(err);
                });
            }
        });
    }
    fetchDisabilityDropDown(): Promise<{ key: number; value: string; description: string }[]> {
        return new Promise((resolve, reject) => {
            if (this.disabilityDropdown.length) {
                resolve(this.disabilityDropdown);
            } else {
                this._httpService.Get(commonDdUrls.disabilityType).subscribe((response) => {
                    this.disabilityDropdown = _.sortBy(response.map((mapper: { disabilityId: number; disabilityName: string; disabilityDescription: string }) => ({
                        value: mapper.disabilityName, key: mapper.disabilityId, description: mapper.disabilityDescription
                    })), [e => e.value.toLowerCase()]);
                    resolve(this.disabilityDropdown);
                }, err => {
                    reject(err);
                });
            }
        });
    }
    fetchLanguageDropDown(): Promise<TKeyValue<number, string>[]> {
        return new Promise((resolve, reject) => {
            if (this.languageDropdown.length) {
                resolve(this.languageDropdown);
            } else {
                this._httpService.Get(commonDdUrls.language).subscribe((response) => {
                    this.languageDropdown = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                        value: mapper.text, key: mapper.id,
                    })), [e => e.value.toLowerCase()]);
                    resolve(this.languageDropdown);
                }, err => {
                    reject(err);
                });
            }
        });
    }
    fetchLanguageProficiencyDropDown(): Promise<TKeyValue<number, string>[]> {
        return new Promise((resolve, reject) => {
            if (this.languageProficiencyDropdown.length) {
                resolve(this.languageProficiencyDropdown);
            } else {
                this._httpService.Get(commonDdUrls.languageProficiency).subscribe((response) => {
                    this.languageProficiencyDropdown = _.sortBy(response.map((mapper: { id: number; text: string }) => ({
                        value: mapper.text, key: mapper.id,
                    })), [e => e.value.toLowerCase()]);
                    resolve(this.languageProficiencyDropdown);
                }, err => {
                    reject(err);
                });
            }
        });
    }

    reset() {
        this.moduleDd = [];
        this.currencyDd = [];
        this.currencyDropDownRequest = null;
        this.genderDd = [];
        this.skillsDd = [];
        this.bloodGroupsDd = [];
        this.nationalityDd = [];
        this.employeeTypeDd = [];
        this.employeeStatusDd = [];
        this.specificEmpStatusDd = [];
        this.specificEmpOtherStatusDd = [];
        this.salutationDd = [];
        this.maritalStatusDd = [];
        this.parentsMediclaimDd = [];
        this.qualificationDd = [];
        this.qualificationTypeDd = [];
        this.specializationDd = [];
        this.nominationTypeDd = [];
        this.relationshipTypeDd = [];
        this.documentTypeDd = [];
        this.industryTypeDd = [];
        this.monthDd = [];
        this.monthsDd = [];  // genral
        this.monthOnlyDd = [];
        this.yearOnlyDd = [];
        this.payModeDd = [];
        this.bankDd = [];
        this.branchBankWiseDd = [];
        this.religionDd = [];
        this.weekDaysDd = [];
        this.financialYearDd = [];
        this.currentFinancialYear = null;
        this.financialYearListDd = [];
        this.financialYearListRequest = null;
    }

}
export interface MonthsYearModel {
    year: number;
    monthId: number;
    monthName: string;
    date: string;
    moment: moment.Moment;
    label: string
}
export interface FinancialYearDDModel {
    key: number;
    value: string;
    startDate: string;
    endDate: string;
    currentFinancialYear: boolean
}
export interface BankBranchDropdownModel {
    bankBranchCode: string;
    branchName: string;
    alias: string;
    accountNumber: string;
    reportId: number;
    bankCode: string;
    bankName: string;
    magneticInkCharacterRecognitionCode: string;
    branchLocation: string;
    branchAddress: string;
    branchBasicStatisticalReturnsCode: string;
    internationalBankAccountNumber: string;
    indianFinancialSystemCode: string;
    isActive: boolean;
    isCustomReport: boolean;
    customReport: number;
    transactionType: string;
    paymentMode: string;
    merchantId: string;
    merchantKey: string;
    disbursalAccount: string;
    employeeForOnetimePassword: number;
    paymentDetailsVerified: boolean;
    walletId: string;
    paymentServiceProvider: string;
    providerBank: string;
    consentElectronicallyReleaseSalary: boolean;
    consentManuallyReleaseSalary: boolean;
}
export interface BankBranchDDModel {
    key: string;
    value: string;
    ifsc: string;
    bsrc: string;
}